const CART_ALLERGEN_ACK_ATTRIBUTE = 'アレルゲン・原材料情報の確認';
const CART_ALLERGEN_ACK_VALUE = '確認済み';

class CartAllergenAck extends HTMLElement {
  constructor() {
    super();

    this.checkbox = this.querySelector('.cart-allergen-ack__checkbox');
    this.checkoutButton = document.getElementById('checkout');
    this.cartForm = document.getElementById('cart');
    this.liveRegion = this.querySelector('[data-cart-allergen-live]');
    this.errorMessage = this.querySelector('[data-cart-allergen-error]');
    this.resetMessage = this.dataset.resetMessage || '';
    this.requiredMessage = this.dataset.requiredMessage || '';
    this.saveErrorMessage = this.dataset.saveErrorMessage || '';
    this.isUpdatingAttribute = false;
    this.attributeRequestSequence = 0;

    this.onCheckboxChange = this.onCheckboxChange.bind(this);
    this.onFormSubmit = this.onFormSubmit.bind(this);
    this.onCartUpdate = this.onCartUpdate.bind(this);

    if (this.checkbox) {
      this.checkbox.addEventListener('change', this.onCheckboxChange);
    }

    if (this.cartForm) {
      this.cartForm.addEventListener('submit', this.onFormSubmit);
    }

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate);

    this.syncCheckboxDisabledState();
    this.syncCheckoutState(false);
    this.hideError();
    this.clearAcknowledgementOnInit();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  isCartEmpty() {
    return document.getElementById('main-cart-footer')?.classList.contains('is-empty');
  }

  clearAcknowledgementOnInit() {
    if (this.isCartEmpty()) return;

    if (this.checkbox) {
      this.checkbox.checked = false;
    }

    this.syncCheckoutState(false);
    this.beginAttributeUpdate();

    this.updateAttribute('')
      .then((requestId) => {
        this.finishAttributeUpdateIfLatest(requestId);
      })
      .catch((error) => {
        if (error?.name === 'StaleAttributeUpdate') return;
        this.finishAttributeUpdateIfLatest(error.requestId);
      });
  }

  onCartUpdate(event) {
    if (!event || event.source !== 'cart-items') return;
    this.syncCheckboxDisabledState();
    this.resetAcknowledgement(true);
  }

  onCheckboxChange() {
    if (!this.checkbox || this.isUpdatingAttribute) return;

    if (this.checkbox.checked) {
      this.saveAcknowledgement();
      return;
    }

    this.clearAcknowledgement(false);
  }

  saveAcknowledgement() {
    this.beginAttributeUpdate();
    this.syncCheckoutState(false);
    this.hideError();

    this.updateAttribute(CART_ALLERGEN_ACK_VALUE)
      .then((requestId) => {
        if (!this.isLatestAttributeRequest(requestId)) return;
        if (!this.checkbox?.checked) {
          this.endAttributeUpdate();
          this.syncCheckoutState(false);
          return;
        }
        this.endAttributeUpdate();
        this.syncCheckoutState(true);
        this.hideError();
      })
      .catch((error) => {
        if (error?.name === 'StaleAttributeUpdate') return;
        if (!this.isLatestAttributeRequest(error.requestId)) return;
        if (this.checkbox) {
          this.checkbox.checked = false;
        }
        this.endAttributeUpdate();
        this.syncCheckoutState(false);
        this.showError(this.saveErrorMessage);
      });
  }

  clearAcknowledgement(notify) {
    if (this.checkbox) {
      this.checkbox.checked = false;
    }

    this.syncCheckoutState(false);
    this.beginAttributeUpdate();

    this.updateAttribute('')
      .then((requestId) => {
        this.finishAttributeUpdateIfLatest(requestId, () => {
          if (notify) {
            this.announce(this.resetMessage);
          }
        });
      })
      .catch((error) => {
        if (error?.name === 'StaleAttributeUpdate') return;
        if (!this.isLatestAttributeRequest(error.requestId)) return;
        this.showError(this.saveErrorMessage);
        this.finishAttributeUpdateIfLatest(error.requestId, () => {
          if (notify) {
            this.announce(this.resetMessage);
          }
        });
      });
  }

  resetAcknowledgement(notify = false) {
    this.clearAcknowledgement(notify);
  }

  beginAttributeUpdate() {
    this.isUpdatingAttribute = true;
    this.syncCheckboxDisabledState();
    this.syncCheckoutState(false);
  }

  endAttributeUpdate() {
    this.isUpdatingAttribute = false;
    this.syncCheckboxDisabledState();
  }

  onFormSubmit(event) {
    if (this.isUpdatingAttribute) {
      event.preventDefault();
      return;
    }

    if (this.isCartEmpty()) return;

    if (!this.checkbox?.checked) {
      event.preventDefault();
      this.showError(this.requiredMessage);
      this.checkbox?.focus();
      return;
    }

    if (this.checkoutButton?.disabled) {
      event.preventDefault();
    }
  }

  syncCheckoutState(enabled) {
    const checkoutButton = this.checkoutButton;
    if (!checkoutButton) return;

    const isEnabled =
      !this.isCartEmpty() &&
      !this.isUpdatingAttribute &&
      enabled &&
      this.checkbox?.checked;

    checkoutButton.disabled = !isEnabled;
    checkoutButton.setAttribute('aria-disabled', isEnabled ? 'false' : 'true');
  }

  syncCheckboxDisabledState() {
    if (!this.checkbox) return;
    this.checkbox.disabled = this.isCartEmpty() || this.isUpdatingAttribute;
  }

  isLatestAttributeRequest(requestId) {
    return requestId === this.attributeRequestSequence;
  }

  finishAttributeUpdateIfLatest(requestId, callback) {
    if (!this.isLatestAttributeRequest(requestId)) return;
    this.endAttributeUpdate();
    if (callback) callback();
  }

  updateAttribute(value) {
    const requestId = ++this.attributeRequestSequence;
    const body = JSON.stringify({
      attributes: {
        [CART_ALLERGEN_ACK_ATTRIBUTE]: value,
      },
    });

    return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        if (!response.ok) {
          const error = new Error('Cart attribute update failed');
          error.requestId = requestId;
          throw error;
        }
        return response.json();
      })
      .then(() => {
        if (!this.isLatestAttributeRequest(requestId)) {
          const error = new Error('Stale attribute update');
          error.name = 'StaleAttributeUpdate';
          error.requestId = requestId;
          throw error;
        }
        return requestId;
      })
      .catch((error) => {
        if (!error.requestId) {
          error.requestId = requestId;
        }
        throw error;
      });
  }

  showError(message) {
    if (this.errorMessage) {
      this.errorMessage.hidden = false;
      if (message) this.errorMessage.textContent = message;
    }
    this.announce(message || this.requiredMessage);
  }

  hideError() {
    if (this.errorMessage) {
      this.errorMessage.hidden = true;
    }
  }

  announce(message) {
    if (!this.liveRegion || !message) return;
    this.liveRegion.textContent = '';
    window.setTimeout(() => {
      this.liveRegion.textContent = message;
    }, 100);
  }
}

if (!customElements.get('cart-allergen-ack')) {
  customElements.define('cart-allergen-ack', CartAllergenAck);
}
